import { FC, useState, useEffect } from 'react';
import { AnalyzeResponse } from '../../types/analyzeResponse';
import { useAuth } from '../../auth/authContext';
import { useAnalyzeService } from '../../services/useAnalyzeService';
import { userService } from '../../api/userService';
import { useResumeManager } from '../../services/useResumeManager';
import { Resume } from '../../types/resume';
import { ResumeVersion } from '../../types/resumeVersion';
import './useranalyzerpage.css';
import { AnalysisRequest } from '../../types/analysisRequest';
import { AnalysisAIResultPanel } from '../../components/analysisAIResults';
import { AnalyzeAI } from '../../types/analyzeAI';
import { useAnalysisUsage } from '../../services/useAnalysisUsage';
import { NextImprovementResponse } from '../../types/nextImprovements';
import { ImprovementRequest } from '../../types/improvementRequest';
import { AnalysisSuggestionsPanel } from '../../components/analysisSuggestions';

const LIFETIME_LIMIT = 999999;

const UserAnalyzer: FC = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<null | AnalyzeResponse>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [jobId] = useState<string>("");
    const [jobTitle, setJobTitle] = useState<string>("");
    const [jobCompany, setJobCompany] = useState<string>("");
    const [jobSource, setJobSource] = useState<string>("");
    const [jobSourceUrl, setJobSourceUrl] = useState<string>("");
    const [jobPostedDate, setJobPostedDate] = useState<Date>(new Date());
    const [resumeText, setResumeText] = useState<string>("");
    const [jdText, setJdText] = useState<string>("");
    const [, setIsChanged] = useState<boolean>(false);

    // Resume Manager State
    const [isManagerOpen, setIsManagerOpen] = useState(false);
    const [selectedResume, setSelectedResume] = useState<Resume | null>(null);
    const [selectedVersion, setSelectedVersion] = useState<ResumeVersion | null>(null);
    const [availableVersions, setAvailableVersions] = useState<ResumeVersion[]>([]);
    const [originalResumeText, setOriginalResumeText] = useState<string>("");
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
    const [hasDraft, setHasDraft] = useState<boolean>(false);
    const [suggestions, setSuggestions] = useState<NextImprovementResponse | null>(null);
    const [isLoadingSuggestions, setIsLoadingSuggestions] = useState<boolean>(false);
    const [suggestionsError, setSuggestionsError] = useState<string | null>(null);

    const { resumes, refetch } = useResumeManager(user?.id);
    const { limits, refetch: refetchLimits } = useAnalysisUsage(user?.id);
    const {
        getOpenAIAnalysisResults,
        submitJobPostingForAIAnalysis,
        submitAnalysisAIResult,
        getSuggestions,
        suggestionsLoading,
        suggestionsError: suggestionsServiceError,
    } = useAnalyzeService();

    // Load versions when resume is selected
    useEffect(() => {
        if (selectedResume) {
            // TODO: Fetch all versions for this resume from API
            // For now, mock with single version
            const versions: ResumeVersion[] = selectedResume.resumeVersions;
            setAvailableVersions(versions); // Filter out drafts based on your logic
            
            //   
        }
    }, [selectedResume]);

    // Check for unsaved changes
    useEffect(() => {
        if (originalResumeText) {
            setHasUnsavedChanges(resumeText !== originalResumeText);
        }
    }, [resumeText, originalResumeText]);

    // Clear any messages
    useEffect(() => {
        setMessage(null);
    }, [isManagerOpen]);

    const handleResumeSelect = (resumeId: string) => {
        const resume = resumes.find(r => r.resumeId === resumeId);
        if (resume) {
            setSelectedResume(resume);
        }
    };

    const handleVersionSelect = (versionId: string) => {
        const version = availableVersions.find(v => v.resumeVersionId === versionId);
        if (version) {
            setSelectedVersion(version);
            setResumeText(version.resumeText);
            setOriginalResumeText(version.resumeText);
            setHasUnsavedChanges(false);
            setIsManagerOpen(false);
        }
    };

    const handleQuickSave = async () => {
        // Save as draft version
        if (!selectedVersion || !user?.id) return;
        
        try {
            const { saveDraftToVersion } = userService();
            await saveDraftToVersion(user.id, selectedVersion.resumeVersionId);
            
            setOriginalResumeText(resumeText);
            setHasUnsavedChanges(false);
            setHasDraft(true);
            await refetch();

            // Force re-sync: clear and re-select the resume to trigger useEffect
            if (selectedResume) {
                const updatedResume = resumes.find(r => r.resumeId === selectedResume.resumeId);
                if (updatedResume) {
                    setSelectedResume(updatedResume);
                }
            }

            setIsManagerOpen(false);
            setMessage("✓ Draft saved successfully");
        } catch (e: any) {
            setMessage("ERROR: Failed to save draft");
        }
    };

    const handleSaveAsNew = async () => {
        const resumeName = prompt("Enter name for new resume:");
        if (!resumeName || !user?.id) return;

        try {
            const { saveToResume } = userService();
            await saveToResume(user.id, resumeName, selectedVersion?.resumeVersionId || "");
            await refetch();

            setIsManagerOpen(false);
            setMessage("✓ New resume created successfully");
        } catch (e: any) {
            setMessage("ERROR: Failed to create new resume");
        }
    };

    const clearJobDescription = () => {
        setJobTitle("");
        setJobCompany("");
        setJobSource("");
        setJobSourceUrl("");
        setJobPostedDate(new Date());
        setJdText("");
        setIsChanged(false);
    };

    const analyze = async () => {
        const jd = jdText;
        const resume = resumeText;
        setJobPostedDate(new Date());

        const payload: AnalysisRequest = {
            JobId: jobId === "" ? "" : jobId,
            JobTitle: jobTitle,
            JobCompany: jobCompany,
            JobSource: jobSource,
            JobSourceUrl: jobSourceUrl,
            JobPostedDate: jobPostedDate.toLocaleDateString(),
            JdText: jd,
            ResumeText: resume,
            UserId: user!.id,
            Fingerprint: localStorage.getItem('clientFingerprint') || "",
            ResumeVersionId: selectedVersion?.resumeVersionId || "",
        }

        console.log("Submitting job posting for AI analysis:", payload);
        const jobpostingId: string = await submitJobPostingForAIAnalysis(payload);
        console.log("Job posting submitted for AI analysis:", jobpostingId);
        
        const aiPayload = {
            job_description: jd,
            resume: resume,
            llm_model: "",
            user_id: user!.id
        }
        
        setMessage(null);
        setLoading(true);
        setResult(null);
        setSuggestions(null);
        setSuggestionsError(null);

        try {
            console.log("Requesting AI analysis with payload:", aiPayload);
            const resp: AnalyzeResponse = await getOpenAIAnalysisResults(aiPayload);
            const aiResult: AnalyzeAI = {
                analysisId: resp.analysis_id,
                userId: user!.id,
                jobPostingId: jobpostingId,
                prompt_tokens: resp.usage.prompt_tokens,
                completion_tokens: resp.usage.completion_tokens,
                total_tokens: resp.usage.total_tokens,
                estimated_cost: resp.usage.estimated_cost,
                promptResponseJson: JSON.stringify(resp),
                llmModel: resp.meta?.model || "",
                promptVersion: resp.meta?.prompt_version || "",
                isCached: resp.cached,
                resumeVersionId: selectedVersion?.resumeVersionId || "",
                resumeTextUsed: resume,
                durationMs: resp.durationMs || 0,
            };

            await submitAnalysisAIResult(aiResult);
            setIsChanged(false);        
            setResult(resp);
            await refetchLimits();
        } catch (e: any) {
            setMessage(e.message || "ERROR: Failed to reach API");
        } finally {
            setLoading(false);
        }
    };

    const handleGetSuggestions = async () => {
        if (!result?.analysis_id) return;

        setIsLoadingSuggestions(true);
        setSuggestionsError(null);

        const req: ImprovementRequest = {
            job_description: jdText,
            resume: resumeText,
            first_pass: result,
            user_id: user!.id,
            llm_model: ""
        };

        try {
            const response = await getSuggestions(req);
            setSuggestions(response);
        } catch (error: any) {
            setSuggestionsError(error?.message || "Failed to generate suggestions.");
        } finally {
            setIsLoadingSuggestions(false);
        }
    };

    return (
        <>
            {limits && (
                <div className="usage-limits-banner">
                    <div className="usage-limits-content">
                        {limits.purchasedLimit >= LIFETIME_LIMIT ? (
                            <div className="usage-limit-item lifetime-badge">
                                <span className="lifetime-icon">&#9733;</span>
                                <span className="lifetime-label">Lifetime</span>
                            </div>
                        ) : (
                            <>
                                <div className="usage-limit-item">
                                    <span className="usage-label">Purchased:</span>
                                    <span className="usage-value">{limits.purchasedLimit}</span>
                                </div>
                                <div className="usage-limit-item">
                                    <span className="usage-label">Rollover:</span>
                                    <span className="usage-value">{limits.rolloverAvailable}</span>
                                </div>
                                <div className="usage-limit-item total">
                                    <span className="usage-label">Available:</span>
                                    <span className="usage-value highlight">{limits.totalAvailable}</span>
                                </div>
                                {limits.usedThisMonth !== undefined && (
                                    <div className="usage-limit-item">
                                        <span className="usage-label">Used:</span>
                                        <span className="usage-value">{limits.usedThisMonth}</span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
            <section className='container user-try'>
                <div className='user-try-grid'>
                    <div>
                        <label>Title</label>
                        <div>
                            <input type="text" className='input-field' placeholder="Job Title" value={jobTitle} onChange={e => setJobTitle(e.target.value)} />
                        </div>
                    </div>
                    <div>
                        <label>Company</label>
                        <div>
                            <input type="text" className='input-field' placeholder="Company Name" value={jobCompany} onChange={e => setJobCompany(e.target.value)} />
                        </div>
                    </div>
                </div>
            </section>
            <section id="try" className="container try">
                <div className='try-grid'>
                    <div>
                        <label>Job Description:</label>
                        <textarea
                            id="jd"
                            placeholder='Paste job description here...'
                            rows={10}
                            value={jdText}
                            onChange={e => setJdText(e.target.value)}
                        />
                        <button type="button" className='link-btn' onClick={clearJobDescription}>Clear</button>
                    </div>
                    <div>
                        <label>Resume: {selectedResume && `${selectedResume?.resumeTitle} - v${selectedVersion?.version} ${selectedVersion?.isDraft ? " : (Draft)" : ""}`}</label>
                        <textarea 
                            id="resume"
                            placeholder='Paste resume here or load your saved resume...'
                            rows={10}
                            value={resumeText}
                            onChange={e => setResumeText(e.target.value)}
                        />
                        
                        {/* Trigger Button */}
                        <div style={{ display: 'flex' }}>
                            <button 
                                type="button" 
                                className="btn btn-secondary btn-wide" 
                                onClick={() => setIsManagerOpen(true)}
                                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center', marginTop: '10px' }}
                            >
                                <span>📋</span> Resume Manager
                            </button>
                            {hasUnsavedChanges && (
                                <span className="resume-manager-badge" style={{ marginLeft: '0.25rem', marginTop: '13px', width: '100px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    Unsaved
                                </span>
                            )}
                        </div>

                        {/* Modal Dialog */}
                        {isManagerOpen && (
                            <>
                                <div 
                                    className="resume-manager-backdrop" 
                                    onClick={() => setIsManagerOpen(false)}
                                />
                                <div className="resume-manager-modal">
                                    <div className="resume-manager-modal-header">
                                        <div className="resume-manager-modal-title">
                                            <span>📋</span>
                                            <span>Resume Manager</span>
                                            {hasUnsavedChanges && (
                                                <span className="resume-manager-badge">Unsaved Changes</span>
                                            )}
                                            {hasDraft && (
                                                <span className="draft-badge">📝 Version</span>
                                            )}
                                        </div>
                                        <button 
                                            className="modal-close-btn"
                                            onClick={() => setIsManagerOpen(false)}
                                            aria-label="Close"
                                        >
                                            ×
                                        </button>
                                    </div>

                                    <div className="resume-selector-grid">
                                        <div className="resume-field">
                                            <label>Resume</label>
                                            <select 
                                                className="resume-select"
                                                value={selectedResume?.resumeId || ""}
                                                onChange={(e) => handleResumeSelect(e.target.value)}
                                            >
                                                <option value="">Select a resume...</option>
                                                {resumes.map(resume => (
                                                    <option key={resume.resumeId} value={resume.resumeId}>
                                                        {resume.resumeTitle}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="resume-field">
                                            <label>Version</label>
                                            <select 
                                                className="resume-select"
                                                value={selectedVersion?.resumeVersionId || ""}
                                                onChange={(e) => handleVersionSelect(e.target.value)}
                                                disabled={!selectedResume}
                                            >
                                                <option value="">Select version...</option>
                                                {availableVersions.map(version => (
                                                    <option key={version.resumeVersionId} value={version.resumeVersionId}>
                                                        v{version.version} - {new Date(version.createdUtc).toLocaleDateString()} {version.isDraft ? " : (Draft)" : ""}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="resume-actions">
                                        <div className="resume-actions-row">
                                            <button 
                                                className="btn btn-sm btn-primary btn-icon"
                                                onClick={handleQuickSave}
                                            >
                                                <span>💾</span> Save to Version
                                            </button>
                                            <button 
                                                className="btn btn-sm btn-secondary btn-icon"
                                                onClick={handleSaveAsNew}
                                            >
                                                <span>📋</span> Save as Resume
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <div className="analysis-actions-row">
                    <button className="btn btn-primary btn-wide" onClick={analyze} disabled={loading || (limits?.totalAvailable === 0)}>
                        {loading ? "Analyzing..." : "Analyze"}
                    </button>
                    <button
                        className="btn btn-secondary btn-wide"
                        onClick={handleGetSuggestions}
                        disabled={!result || isLoadingSuggestions || suggestionsLoading}
                    >
                        {isLoadingSuggestions || suggestionsLoading
                            ? "Generating suggestions..."
                            : suggestions
                                ? "Refresh Suggestions"
                                : "Improve Resume Match"}
                    </button>
                </div>
                {limits?.totalAvailable === 0 && (
                    <p className='tint small' style={{ color: "#f87171", marginTop: 8 }}>
                        You have reached your analysis limit. Please upgrade your subscription.
                    </p>
                )}
                {message && <p className='tint small' style={{ color: message.startsWith("✓") ? "#34d399" : message.startsWith("ERROR") ? "#f87171" : "#38bdf8", marginTop: 8 }}>{message}</p>}
                {(isLoadingSuggestions || suggestionsLoading) && (
                    <p className='tint small' style={{ color: "#38bdf8", marginTop: 8 }}>
                        Generating suggestions...
                    </p>
                )}
                {(suggestionsError || suggestionsServiceError) && (
                    <div className="suggestions-error" role="alert">
                        <span>{suggestionsError || suggestionsServiceError}</span>
                        <button className="btn btn-sm btn-secondary" onClick={handleGetSuggestions} disabled={!result || isLoadingSuggestions || suggestionsLoading}>
                            Retry
                        </button>
                    </div>
                )}
                {suggestions && (
                    <AnalysisSuggestionsPanel results={suggestions} />
                )}
                {result && (
                    <AnalysisAIResultPanel result={result} />
                )}
            </section>
        </>
    )
}

export default UserAnalyzer;